import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useThumbtackModalContext } from "components/thumbtack-modal";
import { type KeystoneInspection } from "data/keystone-api-mapping";
import {
  useInspectionsList,
  useKeystone,
  usePolicyDetail,
  usePolicyPlatformId,
  useSelectedPolicy,
  useUiEvents,
} from "data/ss-data-hooks";
import { isError } from "data/ss-error";
import { checkForNaStringValue, trackEvent, useFlags } from "utils";

import {
  isQualifyingRoofMarketplaceReferral,
  isRoofMarketplaceEligible,
} from "./roof-marketplace-drawer";
import { useRoofMarketplaceRequestBids } from "./use-roof-marketplace-request-bids";

type UseInspectionsRoofMarketplaceArgs = {
  latestInspection: KeystoneInspection | undefined;
};

/** Shared roof marketplace + Thumbtack routing for inspections pages (new and legacy). */
export function useInspectionsRoofMarketplace({
  latestInspection,
}: UseInspectionsRoofMarketplaceArgs) {
  const { inspectionsRoofMarketplace, inspectionsProofUpload } = useFlags();
  const roofMarketplaceWorkflowEnabled = inspectionsRoofMarketplace === true;
  const [isRoofMarketplaceDrawerOpen, setIsRoofMarketplaceDrawerOpen] =
    useState(false);
  const [roofMarketplaceCanService, setRoofMarketplaceCanService] = useState<
    boolean | null
  >(null);
  const roofMarketplaceCanServiceRef = useRef<boolean | null>(null);
  roofMarketplaceCanServiceRef.current = roofMarketplaceCanService;

  const keystone = useKeystone();
  const platformPolicyId = usePolicyPlatformId();
  const { data: selectedPolicy } = useSelectedPolicy();
  const roofMarketplacePolicyId =
    platformPolicyId ?? selectedPolicy?.platformPolicyId ?? "";
  const { data: inspectionsListData } = useInspectionsList(
    !!inspectionsProofUpload,
  );
  const { data: policyDetail } = usePolicyDetail();
  const { openThumbtackModal } = useThumbtackModalContext();
  const { data: uiEvents, mutate: mutateUiEvents } = useUiEvents();

  const roofMarketplaceEligible =
    isRoofMarketplaceEligible(inspectionsListData);
  const roofMarketplaceDataEligible =
    roofMarketplaceWorkflowEnabled && roofMarketplaceEligible;

  const openRoofMarketplaceOrThumbtack = useCallback(async () => {
    const property = policyDetail?.userDetails?.propertyAddress;
    const addr = {
      line1: (property?.street1 ?? "").trim(),
      city: (property?.city ?? "").trim(),
      state: (property?.state ?? "").trim(),
      zip: (property?.zip ?? "").trim(),
    };
    let canService = roofMarketplaceCanServiceRef.current;
    if (canService === null) {
      const res = await keystone.checkRoofMarketplaceServiceAvailability(addr);
      if (isError(res)) {
        canService = false;
      } else {
        canService = res.canService;
      }
      setRoofMarketplaceCanService(canService);
    }
    const zip = property?.zip ?? "";
    const qualifyingReferral = latestInspection?.review?.referrals?.find(
      isQualifyingRoofMarketplaceReferral,
    );
    const searchTerm =
      checkForNaStringValue(qualifyingReferral?.searchTerm ?? "") ?? "";
    const referralId = qualifyingReferral?.id ?? "";
    const category = qualifyingReferral?.category;
    const thumbtackTracking = {
      referralId,
      category,
      searchTerm: qualifyingReferral?.searchTerm ?? searchTerm,
    };

    if (canService) {
      trackEvent("Inspection write-out: Contractor help clicked", {
        source: "roof_marketplace",
        referralId,
        category,
      });
      trackEvent("Roof Marketplace: Request drawer opened", {
        policyId: roofMarketplacePolicyId,
        referralId,
      });
      setIsRoofMarketplaceDrawerOpen(true);
    } else {
      trackEvent("Inspection write-out: Contractor help clicked", {
        source: "thumbtack",
        referralId,
        category,
      });
      openThumbtackModal(zip, searchTerm, undefined, thumbtackTracking);
    }
  }, [
    keystone,
    latestInspection,
    openThumbtackModal,
    policyDetail,
    roofMarketplacePolicyId,
  ]);

  useEffect(() => {
    if (!roofMarketplaceDataEligible || !policyDetail) {
      setRoofMarketplaceCanService(null);
      return;
    }
    const property = policyDetail.userDetails?.propertyAddress;
    const addr = {
      line1: (property?.street1 ?? "").trim(),
      city: (property?.city ?? "").trim(),
      state: (property?.state ?? "").trim(),
      zip: (property?.zip ?? "").trim(),
    };
    if (!addr.line1 || !addr.city || !addr.state || !addr.zip) {
      setRoofMarketplaceCanService(false);
      return;
    }
    let cancelled = false;
    setRoofMarketplaceCanService(null);
    (async () => {
      const res = await keystone.checkRoofMarketplaceServiceAvailability(addr);
      if (cancelled) {
        return;
      }
      if (isError(res)) {
        setRoofMarketplaceCanService(false);
      } else {
        setRoofMarketplaceCanService(res.canService);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [keystone, policyDetail, roofMarketplaceDataEligible]);

  const qualifyingReferralIds = useMemo(
    () =>
      (latestInspection?.review?.referrals ?? [])
        .filter(isQualifyingRoofMarketplaceReferral)
        .map((r) => r.id),
    [latestInspection?.review?.referrals],
  );

  const storedReferralIds = useMemo((): string[] => {
    const rm = (uiEvents?.events as Record<string, unknown> | undefined)
      ?.roofMarketplace;
    if (rm && typeof rm === "object" && "referralIds" in rm) {
      const ids = (rm as { referralIds: unknown }).referralIds;
      if (Array.isArray(ids)) {
        return ids as string[];
      }
    }
    return [];
  }, [uiEvents?.events]);

  const hasExistingBid = useMemo(
    () =>
      qualifyingReferralIds.length > 0 &&
      qualifyingReferralIds.every((id) => storedReferralIds.includes(id)),
    [qualifyingReferralIds, storedReferralIds],
  );

  const requestRoofMarketplaceBids = useRoofMarketplaceRequestBids({
    qualifyingReferralIds,
    roofMarketplacePolicyId,
    mutateUiEvents,
  });

  const closeRoofMarketplaceDrawer = useCallback(() => {
    setIsRoofMarketplaceDrawerOpen(false);
  }, []);

  return {
    roofMarketplaceWorkflowEnabled,
    roofMarketplaceDataEligible,
    roofMarketplaceCanService,
    openRoofMarketplaceOrThumbtack,
    isRoofMarketplaceDrawerOpen,
    closeRoofMarketplaceDrawer,
    hasExistingBid,
    requestRoofMarketplaceBids,
  };
}
